import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@mantine/core";
import services from "../../services";
import { useState } from "react";
import TableSkeleton from "../Skeleton/TableSkeleton";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  IconFileSpreadsheet,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { exportToExcel } from "react-json-to-excel";
import { useSearchParams } from "react-router-dom";
import BarLoading from "../Loading/BarLoading";
import { currencyFormatter } from "../../utils/helper";

dayjs.extend(customParseFormat);
dayjs.locale("tr");

const Financial = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [title] = useState(searchParams.get("title") || "");
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs());

  const { isLoading, error, data, isFetching } = useQuery({
    queryKey: ["getFinancialReport", currentMonth.format("YYYY-MM")],
    queryFn: async () => {
      const res = await services
        .getBasaksehirFinancial(currentMonth.format("DD-MM-YYYY"))
        .then((res) => res.data);

      if (res.start_date) {
        const startDate = dayjs(res.start_date, "DD MMMM YYYY", "tr");

        if (startDate.isValid()) {
          setStartDate(startDate);
        }
      }

      return res;
    },
  });

  const dataMap = data?.data?.map((item) => {
    return {
      [t("product-report.product-service")]: item.product_name,
      [t("product-report.amount")]: item.count,
      [t("product-report.unit-price")]: item.single_price,
      [t("product-report.vat-rate")]: item.kdv,
      [t("product-report.vat-amount")]: item.kdv_amount,
      [t("product-report.product-service-amount")]: item.total_price,
    };
  });

  const handlePreviousMonth = () => {
    const newMonth = currentMonth.subtract(1, "month");
    setCurrentMonth(newMonth);
  };

  const handleNextMonth = () => {
    const baseMonth = startDate.isValid() ? startDate : currentMonth;

    const newMonth = baseMonth.add(1, "month");

    setCurrentMonth(newMonth);
  };

  const isCurrentMonth = startDate.isSame(dayjs(), "month");
  const monthDisplayText = isCurrentMonth
    ? t("labels.this-month")
    : startDate.format("MMM YYYY");

  const titleDescMap = {
    title:
      t("labels.financial") +
      " " +
      startDate.format("MMMM YYYY") +
      (title ? " - " + title : ""),
    description: t("descriptions.financial-report", {
      start_date: data?.start_date,
      end_date: data?.end_date,
    }),
  };

  if (error) return <p>Error :(</p>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 md:pb-16 w-full ">
      {isLoading ? (
        <div className="flex justify-center items-center h-[100px]">
          <BarLoading />
        </div>
      ) : (
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 dark:text-white text-slate-700 sm:text-lg sm:leading-7 whitespace-pre-wrap w-[80%]">
              {titleDescMap.title}
            </h1>
            <p className="mt-2 text-sm dark:text-gray-400 text-gray-500">
              {titleDescMap.description}
            </p>
          </div>

          <div className="flex justify-center items-center gap-2">
            {!isLoading && (
              <div className=" flex justify-end items-center">
                <Button
                  type="button"
                  size="xs"
                  radius="md"
                  className="gap-2 mt-4 "
                  color="gray"
                  onClick={() =>
                    exportToExcel(
                      dataMap,
                      `financial-report-${currentMonth.format("YYYY-MM")}`
                    )
                  }
                >
                  <div className="flex items-center justify-start gap-2">
                    <IconFileSpreadsheet />
                    <span>{t("actions.download-excel")}</span>
                  </div>
                </Button>
              </div>
            )}
            <div className="flex justify-center items-start mt-4 md:justify-end">
              <div className="mt-4 sm:mt-0 sm:flex-none flex flex-row items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg  max-w-[200px]">
                <button
                  type="button"
                  onClick={handlePreviousMonth}
                  className="p-1 ml-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <IconChevronLeft
                    size={16}
                    className="text-gray-600 dark:text-gray-300"
                  />
                </button>

                <div className="px-3 py-1.5 min-w-[120px] text-center">
                  <span className="text-sm font-medium dark:text-white text-slate-700">
                    {monthDisplayText}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="p-1 mr-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <IconChevronRight
                    size={16}
                    className="text-gray-600 dark:text-gray-300"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flow-root mb-6 w-full px-2">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="dark:bg-gray-800 bg-gray-300 text-slate-700">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 w-[150px] text-slate-700 sm:pl-6"
                    >
                      {t("product-report.product-service")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-gray-300 text-slate-700"
                    >
                      {t("product-report.amount")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-gray-300 text-slate-700"
                    >
                      {t("product-report.unit-price")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-gray-300 text-slate-700"
                    >
                      {t("product-report.vat-rate")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-gray-300 text-slate-700"
                    >
                      {t("product-report.vat-amount")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-gray-300 text-slate-700"
                    >
                      {t("product-report.product-service-amount")}
                    </th>
                    <th
                      scope="col"
                      className="flex py-3.5 pl-3 pr-4 sm:pr-6"
                    ></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:bg-gray-800 bg-gray-200 text-slate-700 w-full">
                  {isLoading || isFetching ? (
                    <TableSkeleton colSize={7} />
                  ) : (
                    data?.data?.map((company, index) => (
                      <tr
                        key={`${company.name}-${index.toString()}`}
                        className={`${
                          index % 2 === 0
                            ? "dark:bg-slate-700/80 bg-gray-200"
                            : "dark:bg-slate-600/80 bg-gray-100"
                        } hover:bg-orange-200/90 dark:hover:bg-orange-600/20`}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 w-[100px] text-slate-700 sm:pl-4">
                          {company.product_name}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm dark:text-gray-300 text-gray-500">
                          {company.count}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm dark:text-gray-300 text-gray-500">
                          {currencyFormatter(company.single_price)}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm dark:text-gray-300 text-gray-500">
                          {company.kdv}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          {currencyFormatter(company.kdv_amount)}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          {currencyFormatter(company.total_price)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium w-[150px] sm:pr-6 "></td>
                      </tr>
                    ))
                  )}
                  {data?.data && data.data.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-8">
                        {t("labels.no-data-found")}
                      </td>
                    </tr>
                  )}
                  {data?.data && data.data.length > 0 && (
                    <tr
                      key="total"
                      className="dark:bg-slate-600/80 bg-gray-100 hover:bg-orange-200/90 dark:hover:bg-orange-600/20"
                    >
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 w-[100px] text-slate-700 sm:pl-4">
                        <strong>{t("labels.total")}</strong>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm dark:text-gray-300 text-gray-500"></td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm dark:text-gray-300 text-gray-500"></td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm dark:text-gray-300 text-gray-500"></td>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                        <strong>
                          {Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(
                            data?.data?.reduce(
                              (num, a) => (num += Number(a.kdv_amount)),
                              0
                            ) || 0
                          )}
                        </strong>
                      </td>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                        <strong>
                          {Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(
                            data?.data?.reduce(
                              (num, a) => (num += Number(a.total_price)),
                              0
                            ) || 0
                          )}
                        </strong>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium w-[150px] sm:pr-6 "></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Financial;
